import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'system'

interface ThemeState {
  theme: Theme
  isDarkMode: boolean
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  initializeTheme: () => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      isDarkMode: false,
      
      setTheme: (theme: Theme) => {
        set({ theme })
        
        // Apply theme immediately
        const isDark = theme === 'dark' || 
          (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
        
        set({ isDarkMode: isDark })
        
        if (isDark) {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
      },
      
      toggleTheme: () => {
        const currentTheme = get().theme
        const newTheme = currentTheme === 'dark' ? 'light' : 'dark'
        get().setTheme(newTheme)
      },
      
      initializeTheme: () => {
        if (typeof window === 'undefined') {
          return
        }

        try {
          const persisted = localStorage.getItem('theme-storage')

          if (persisted) {
            const parsed = JSON.parse(persisted) as { state?: { theme?: Theme } }
            const storedTheme = parsed?.state?.theme

            if (storedTheme) {
              get().setTheme(storedTheme)
              return
            }
          }
        } catch (error) {
          console.error('Failed to restore theme preference:', error)
        }

        get().setTheme(get().theme)
      }
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({ theme: state.theme }),
    }
  )
)
